/**
 * Parse YouTube / Vimeo / Loom share URLs into safe iframe embed URLs.
 * Unknown hosts return null — never pass arbitrary URLs into an iframe.
 */

export const VIDEO_PROVIDERS = ['youtube', 'vimeo', 'loom'];

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);

const LOOM_HOSTS = new Set(['loom.com', 'www.loom.com']);

/**
 * @param {string} raw
 * @returns {{ provider: 'youtube'|'vimeo'|'loom', originalUrl: string, embedUrl: string, videoId: string } | null}
 */
export const parseVideoUrl = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;

  let url;
  try {
    url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const host = url.hostname.toLowerCase();
  const path = url.pathname || '';

  if (YOUTUBE_HOSTS.has(host)) {
    let id = '';
    if (host === 'youtu.be' || host === 'www.youtu.be') {
      id = path.split('/').filter(Boolean)[0] || '';
    } else {
      const parts = path.split('/').filter(Boolean);
      if (parts[0] === 'watch') id = url.searchParams.get('v') || '';
      else if (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live') {
        id = parts[1] || '';
      } else if (url.searchParams.get('v')) {
        id = url.searchParams.get('v') || '';
      }
    }
    id = id.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 20);
    if (id.length < 6) return null;
    return {
      provider: 'youtube',
      originalUrl: trimmed,
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
      videoId: id,
    };
  }

  if (VIMEO_HOSTS.has(host)) {
    const parts = path.split('/').filter(Boolean);
    let id = '';
    if (host === 'player.vimeo.com' && parts[0] === 'video') id = parts[1] || '';
    else id = parts.find((p) => /^\d+$/.test(p)) || '';
    if (!/^\d+$/.test(id)) return null;
    return {
      provider: 'vimeo',
      originalUrl: trimmed,
      embedUrl: `https://player.vimeo.com/video/${id}`,
      videoId: id,
    };
  }

  if (LOOM_HOSTS.has(host)) {
    const parts = path.split('/').filter(Boolean);
    let id = '';
    if (parts[0] === 'share' || parts[0] === 'embed') id = parts[1] || '';
    id = id.replace(/[^a-zA-Z0-9]/g, '');
    if (id.length < 6) return null;
    return {
      provider: 'loom',
      originalUrl: trimmed,
      embedUrl: `https://www.loom.com/embed/${id}`,
      videoId: id,
    };
  }

  return null;
};

/** True when the whole string is a single embeddable video URL. */
export const isSingleVideoUrl = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  return !!parseVideoUrl(trimmed);
};

export const providerLabel = (provider) => {
  switch (provider) {
    case 'youtube':
      return 'YouTube';
    case 'vimeo':
      return 'Vimeo';
    case 'loom':
      return 'Loom';
    default:
      return 'Video';
  }
};

export const embedPlaceholder = (provider) => {
  switch (provider) {
    case 'youtube':
      return 'Paste a YouTube link…';
    case 'vimeo':
      return 'Paste a Vimeo link…';
    case 'loom':
      return 'Paste a Loom link…';
    default:
      return 'Paste a YouTube, Vimeo, or Loom link…';
  }
};

/** Temp marker on an <a> while the Link|Embed paste tab is open. */
export const VIDEO_PASTE_MARK = 'data-video-paste-choice';

/** Drop the temporary Link|Embed marker (and optionally the whole anchor). */
export const stripVideoPasteChoiceAnchor = (html, { removeAnchor = false } = {}) => {
  if (!html || !html.includes(VIDEO_PASTE_MARK)) return html || '';
  const host = document.createElement('div');
  host.innerHTML = html;
  host.querySelectorAll(`a[${VIDEO_PASTE_MARK}]`).forEach((a) => {
    if (removeAnchor) {
      a.remove();
    } else {
      a.removeAttribute(VIDEO_PASTE_MARK);
    }
  });
  return host.innerHTML;
};
